/** SPEC §18: opmerkingen, bijlagen en @vermeldingen bij een taak of project. */
import { useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { tekstDelen } from '@ep/core';
import { api } from '../lib/api';
import { useIk } from '../lib/data';
import { Icoon } from './Icoon';
import { toast } from './toast';

interface Bijlage { id: string; naam: string; type: string; grootte: number; door: string; doorNaam: string | null; aangemaakt: string }
interface Opmerking { id: string; auteurNaam: string; auteurEmail: string; tekst: string; aangemaakt: string; bewerkt: string | null; eigen: boolean; bijlagen: Bijlage[] }
interface Antwoord { opmerkingen: Opmerking[]; bijlagen: Bijlage[]; gebruikers: { id: string; naam: string }[]; ik: string; beheerder: boolean }

const MAX = 10 * 1024 * 1024;
const VOORBEELD = /^image\/(png|jpeg|gif|webp)$/;
const grootte = (b: number) => (b < 1024 ? `${b} B` : b < 1048576 ? `${Math.round(b / 1024)} kB` : `${(b / 1048576).toFixed(1).replace('.', ',')} MB`);
const wanneer = (t: string) => {
  const d = new Date(t);
  const min = Math.round((Date.now() - d.getTime()) / 60000);
  if (min < 1) return 'zojuist';
  if (min < 60) return `${min} min geleden`;
  if (min < 24 * 60 && d.toDateString() === new Date().toDateString()) return `vandaag ${d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}`;
  return d.toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};
const initialen = (n: string) => n.split(/\s+/).filter(Boolean).map((x) => x[0]).slice(0, 2).join('').toUpperCase();

/** Opgeslagen tekst → tekst in het invoerveld (@Naam) + gekozen vermeldingen. */
const naarInvoer = (t: string) => {
  const gekozen: { naam: string; id: string }[] = [];
  const tekst = t.replace(/@\[([^\]\n]{1,80})\]\(([A-Za-z0-9-]{1,64})\)/g, (_m, naam: string, id: string) => {
    gekozen.push({ naam, id });
    return `@${naam}`;
  });
  return { tekst, gekozen };
};
/** Invoer → opgeslagen tekst: alleen gekozen namen worden een vermelding. */
const naarOpslag = (tekst: string, gekozen: { naam: string; id: string }[]) =>
  [...gekozen].sort((a, b) => b.naam.length - a.naam.length).reduce((t, g) => t.split(`@${g.naam}`).join(`@[${g.naam}](${g.id})`), tekst);

function Tekst({ tekst }: { tekst: string }) {
  return (
    <div className="opm-tekst">
      {tekstDelen(tekst).map((d, i) =>
        d.soort === 'vermelding' ? <span key={i} className="vermelding">@{d.naam}</span> : d.soort === 'link' ? <a key={i} href={d.url} target="_blank" rel="noreferrer noopener">{d.url}</a> : <span key={i}>{d.tekst}</span>,
      )}
    </div>
  );
}

function Bijlagen({ lijst, magWeg, onWeg }: { lijst: Bijlage[]; magWeg: (b: Bijlage) => boolean; onWeg: (b: Bijlage) => void }) {
  if (!lijst.length) return null;
  return (
    <div className="opm-bijlagen">
      {lijst.map((b) => (
        <div key={b.id} className="opm-bijlage">
          {VOORBEELD.test(b.type) ? (
            <a href={`/api/bijlagen/${b.id}?inline=1`} target="_blank" rel="noreferrer"><img src={`/api/bijlagen/${b.id}?inline=1`} alt={b.naam} loading="lazy" /></a>
          ) : (
            <Icoon naam="file-text" maat={18} />
          )}
          <a href={`/api/bijlagen/${b.id}`} download={b.naam} className="groei" title={`${b.naam} · ${grootte(b.grootte)}`}>
            <span className="opm-bijlage-naam">{b.naam}</span>
            <span className="klein mute">{grootte(b.grootte)}</span>
          </a>
          {magWeg(b) && <button className="icoon-knop" title="Verwijderen" onClick={() => onWeg(b)}><Icoon naam="x" maat={14} /></button>}
        </div>
      ))}
    </div>
  );
}

/** Invoer met @-vermeldingen en bestanden. */
function Invoer({ gebruikers, start, bijlagenAan, bezig, onVerstuur, onAnnuleer, entiteit, id }: {
  gebruikers: { id: string; naam: string }[];
  start?: string;
  bijlagenAan: boolean;
  bezig: boolean;
  onVerstuur: (tekst: string, bijlageIds: string[]) => Promise<boolean>;
  onAnnuleer?: () => void;
  entiteit: string;
  id: string;
}) {
  const begin = useMemo(() => naarInvoer(start ?? ''), [start]);
  const [tekst, setTekst] = useState(begin.tekst);
  const [gekozen, setGekozen] = useState(begin.gekozen);
  const [zoek, setZoek] = useState<{ q: string; van: number } | null>(null);
  const [sel, setSel] = useState(0);
  const [bestanden, setBestanden] = useState<Bijlage[]>([]);
  const [upload, setUpload] = useState(0);
  const ref = useRef<HTMLTextAreaElement>(null);
  const bestandRef = useRef<HTMLInputElement>(null);
  const kandidaten = zoek ? gebruikers.filter((u) => u.naam.toLowerCase().includes(zoek.q.toLowerCase())).slice(0, 6) : [];

  const wijzig = (v: string, caret: number) => {
    setTekst(v);
    const m = v.slice(0, caret).match(/(^|\s)@([^\s@]{0,30})$/);
    setZoek(m ? { q: m[2], van: caret - m[2].length - 1 } : null);
    setSel(0);
  };
  const kies = (u: { id: string; naam: string }) => {
    if (!zoek) return;
    const caret = ref.current?.selectionStart ?? tekst.length;
    const nieuw = `${tekst.slice(0, zoek.van)}@${u.naam} ${tekst.slice(caret)}`;
    setTekst(nieuw);
    setGekozen((g) => (g.some((x) => x.id === u.id) ? g : [...g, u]));
    setZoek(null);
    requestAnimationFrame(() => {
      const p = zoek.van + u.naam.length + 2;
      ref.current?.focus();
      ref.current?.setSelectionRange(p, p);
    });
  };
  const verstuur = async () => {
    if (bezig || upload) return;
    if (await onVerstuur(naarOpslag(tekst.trim(), gekozen), bestanden.map((b) => b.id))) {
      setTekst('');
      setGekozen([]);
      setBestanden([]);
    }
  };
  const toets = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (zoek && kandidaten.length) {
      if (e.key === 'ArrowDown') return e.preventDefault(), setSel((sel + 1) % kandidaten.length);
      if (e.key === 'ArrowUp') return e.preventDefault(), setSel((sel - 1 + kandidaten.length) % kandidaten.length);
      if (e.key === 'Enter' || e.key === 'Tab') return e.preventDefault(), kies(kandidaten[sel]);
      if (e.key === 'Escape') return e.preventDefault(), e.stopPropagation(), setZoek(null);
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void verstuur();
    }
  };
  const voegToe = async (files: FileList | null) => {
    for (const f of Array.from(files ?? [])) {
      if (f.size > MAX) {
        toast(`${f.name} is groter dan 10 MB`, 'fout');
        continue;
      }
      setUpload((n) => n + 1);
      try {
        const data = await new Promise<string>((ok, nee) => {
          const r = new FileReader();
          r.onload = () => ok(String(r.result));
          r.onerror = () => nee(r.error);
          r.readAsDataURL(f);
        });
        const b = await api.post<Bijlage>('/api/bijlagen', { entiteit, id, naam: f.name, type: f.type || 'application/octet-stream', data });
        setBestanden((x) => [...x, b]);
      } catch (e) {
        toast((e as Error).message, 'fout');
      } finally {
        setUpload((n) => n - 1);
      }
    }
    if (bestandRef.current) bestandRef.current.value = '';
  };
  return (
    <div className="opm-invoer">
      <div style={{ position: 'relative' }}>
        <textarea
          ref={ref}
          rows={start ? 3 : 2}
          value={tekst}
          placeholder="Schrijf een opmerking… typ @ om iemand te noemen"
          maxLength={5000}
          onChange={(e) => wijzig(e.target.value, e.target.selectionStart)}
          onKeyDown={toets}
          onBlur={() => setTimeout(() => setZoek(null), 150)}
        />
        {zoek && kandidaten.length > 0 && (
          <div className="vermeld-lijst" role="listbox">
            {kandidaten.map((u, i) => (
              <button key={u.id} type="button" className={i === sel ? 'aan' : ''} onMouseDown={(e) => { e.preventDefault(); kies(u); }}>
                <span className="opm-avatar klein">{initialen(u.naam)}</span> {u.naam}
              </button>
            ))}
          </div>
        )}
      </div>
      <Bijlagen lijst={bestanden} magWeg={() => true} onWeg={(b) => { void api.del(`/api/bijlagen/${b.id}`); setBestanden((x) => x.filter((y) => y.id !== b.id)); }} />
      <div className="rij">
        {bijlagenAan && (
          <>
            <input ref={bestandRef} type="file" multiple hidden onChange={(e) => void voegToe(e.target.files)} />
            <button type="button" className="knop klein" onClick={() => bestandRef.current?.click()} disabled={!!upload}>
              <Icoon naam="paperclip" maat={14} /> {upload ? 'Uploaden…' : 'Bestand'}
            </button>
          </>
        )}
        <span className="klein mute groei">{tekst.length > 4500 ? `${5000 - tekst.length} tekens over` : 'Ctrl/⌘ + Enter om te plaatsen'}</span>
        {onAnnuleer && <button type="button" className="knop klein" onClick={onAnnuleer}>Annuleren</button>}
        <button type="button" className="knop primair klein" disabled={bezig || !!upload || (!tekst.trim() && !bestanden.length)} onClick={() => void verstuur()}>
          <Icoon naam="send" maat={14} /> {start ? 'Opslaan' : 'Plaatsen'}
        </button>
      </div>
    </div>
  );
}

export function Gesprek({ entiteit, id }: { entiteit: 'TAAK' | 'PROJECT'; id: string }) {
  const qc = useQueryClient();
  const mijnEmail = useIk().data?.email;
  const sleutel = ['opmerkingen', entiteit, id];
  const q = useQuery({ queryKey: sleutel, queryFn: () => api.get<Antwoord>(`/api/opmerkingen?entiteit=${entiteit}&id=${id}`), refetchInterval: 60000 });
  const [bezig, setBezig] = useState(false);
  const [bewerk, setBewerk] = useState<string | null>(null);
  const ververs = () => qc.invalidateQueries({ queryKey: sleutel });
  const doe = async (f: () => Promise<unknown>) => {
    setBezig(true);
    try {
      await f();
      await ververs();
      return true;
    } catch (e) {
      toast((e as Error).message, 'fout');
      return false;
    } finally {
      setBezig(false);
    }
  };
  const a = q.data;
  if (q.isError) return null;
  return (
    <div className="gesprek">
      <div className="rij" style={{ marginBottom: 8 }}>
        <Icoon naam="message-square" maat={18} />
        <h3 style={{ margin: 0 }} className="groei">Opmerkingen{a?.opmerkingen.length ? ` (${a.opmerkingen.length})` : ''}</h3>
      </div>
      {a && <Invoer key="nieuw" gebruikers={a.gebruikers} bijlagenAan bezig={bezig} entiteit={entiteit} id={id} onVerstuur={(tekst, bijlageIds) => doe(() => api.post('/api/opmerkingen', { entiteit, id, tekst, bijlageIds }))} />}
      {a && a.bijlagen.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div className="klein vet mute">Bestanden</div>
          <Bijlagen lijst={a.bijlagen} magWeg={(b) => a.beheerder || b.door === mijnEmail} onWeg={(b) => void doe(() => api.del(`/api/bijlagen/${b.id}`))} />
        </div>
      )}
      <div className="opm-lijst">
        {a?.opmerkingen.map((o) => (
          <div key={o.id} className="opm">
            <span className="opm-avatar">{initialen(o.auteurNaam)}</span>
            <div className="groei" style={{ minWidth: 0 }}>
              <div className="rij" style={{ gap: 6 }}>
                <b>{o.auteurNaam}</b>
                <span className="klein mute groei" title={new Date(o.aangemaakt).toLocaleString('nl-NL')}>{wanneer(o.aangemaakt)}{o.bewerkt ? ' · (bewerkt)' : ''}</span>
                {o.eigen && bewerk !== o.id && <button className="icoon-knop" title="Bewerken" onClick={() => setBewerk(o.id)}><Icoon naam="pencil" maat={14} /></button>}
                {(o.eigen || a.beheerder) && (
                  <button className="icoon-knop" title="Verwijderen" onClick={() => window.confirm('Opmerking verwijderen?') && void doe(() => api.del(`/api/opmerkingen/${o.id}`))}><Icoon naam="trash-2" maat={14} /></button>
                )}
              </div>
              {bewerk === o.id ? (
                <Invoer gebruikers={a.gebruikers} start={o.tekst} bijlagenAan={false} bezig={bezig} entiteit={entiteit} id={id} onAnnuleer={() => setBewerk(null)} onVerstuur={async (tekst) => (await doe(() => api.patch(`/api/opmerkingen/${o.id}`, { tekst }))) && (setBewerk(null), true)} />
              ) : (
                o.tekst && <Tekst tekst={o.tekst} />
              )}
              <Bijlagen lijst={o.bijlagen} magWeg={() => o.eigen || a.beheerder} onWeg={(b) => void doe(() => api.del(`/api/bijlagen/${b.id}`))} />
            </div>
          </div>
        ))}
        {a && !a.opmerkingen.length && <div className="klein mute" style={{ padding: '8px 0' }}>Nog geen opmerkingen. Stel een vraag, deel een bestand of noem een collega met @.</div>}
      </div>
    </div>
  );
}
